// Token管理工具
import { message } from 'antd';
import { refreshAdminToken, type TokenData } from '@/services/fca-api/admin-auth';

const TOKEN_KEY = 'admin_access_token';
const REFRESH_TOKEN_KEY = 'admin_refresh_token';
const TOKEN_TYPE_KEY = 'admin_token_type';
const EXPIRES_KEY = 'admin_token_expires';

export interface TokenInfo {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  expires_at: number; // 过期时间戳
}

/**
 * 保存Token信息
 */
export function setToken(tokenInfo: TokenData): void {
  const expiresAt = Date.now() + tokenInfo.expires_in * 1000;
  
  localStorage.setItem(TOKEN_KEY, tokenInfo.access_token);
  localStorage.setItem(TOKEN_TYPE_KEY, tokenInfo.token_type);
  localStorage.setItem(EXPIRES_KEY, expiresAt.toString());
  
  if (tokenInfo.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokenInfo.refresh_token);
  }
  
  console.log('Token saved successfully', {
    expires_at: new Date(expiresAt),
    expires_in: tokenInfo.expires_in,
  });
}

/**
 * 获取Token信息
 */
export function getToken(): TokenInfo | null {
  const access_token = localStorage.getItem(TOKEN_KEY);
  const refresh_token = localStorage.getItem(REFRESH_TOKEN_KEY);
  const token_type = localStorage.getItem(TOKEN_TYPE_KEY);
  const expires_at = localStorage.getItem(EXPIRES_KEY);
  
  if (!access_token || !token_type || !expires_at) {
    return null;
  }
  
  const expiresAtNumber = parseInt(expires_at, 10);
  const now = Date.now();
  const expires_in = Math.max(0, Math.floor((expiresAtNumber - now) / 1000));
  
  return {
    access_token,
    refresh_token: refresh_token || undefined,
    token_type,
    expires_in,
    expires_at: expiresAtNumber,
  };
}

/**
 * 获取访问Token
 */
export function getAccessToken(): string | null {
  const tokenInfo = getToken();
  return tokenInfo ? tokenInfo.access_token : null;
}

/**
 * 检查Token是否有效
 */
export function isTokenValid(): boolean {
  const tokenInfo = getToken();
  if (!tokenInfo) {
    return false;
  }
  
  // 提前5分钟认为token过期，给刷新留出时间
  const buffer = 5 * 60 * 1000; // 5分钟
  return Date.now() < (tokenInfo.expires_at - buffer);
}

/**
 * 检查Token是否即将过期
 */
export function isTokenExpiringSoon(): boolean {
  const tokenInfo = getToken();
  if (!tokenInfo) {
    return false;
  }
  
  // 提前10分钟开始刷新token
  const buffer = 10 * 60 * 1000; // 10分钟
  return Date.now() > (tokenInfo.expires_at - buffer);
}

/**
 * 清除Token信息
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_TYPE_KEY);
  localStorage.removeItem(EXPIRES_KEY);
  
  console.log('Token cleared');
}

/**
 * 获取Authorization请求头
 */
export function getAuthHeader(): Record<string, string> {
  const tokenInfo = getToken();
  if (!tokenInfo || !isTokenValid()) {
    return {};
  }
  
  return {
    'Authorization': `${tokenInfo.token_type} ${tokenInfo.access_token}`,
  };
}

/**
 * 自动刷新Token（如果需要）
 */
export async function autoRefreshToken(): Promise<boolean> {
  const tokenInfo = getToken();
  if (!tokenInfo || !tokenInfo.refresh_token) {
    return false;
  }
  
  if (!isTokenExpiringSoon()) {
    return true; // Token还未过期，无需刷新
  }
  
  try {
    const response = await refreshAdminToken(tokenInfo.refresh_token);
    
    if (response.success && response.data) {
      setToken(response.data);
      console.log('Token refreshed automatically');
      return true;
    } else {
      throw new Error(response.message || 'Token refresh failed');
    }
  } catch (error) {
    console.error('Failed to refresh token:', error);
    clearToken();
    message.error('登录已过期，请重新登录');
    return false;
  }
}

/**
 * 强制退出登录
 */
export function forceLogout(reason?: string): void {
  console.log('🚨 强制退出登录:', reason || '用户主动退出');
  
  // 清除所有登录相关的本地存储数据
  clearToken();
  
  // 清除可能的其他登录相关数据
  localStorage.removeItem('antd-pro-authority');
  localStorage.removeItem('umi_locale');
  
  // 显示提示信息
  if (reason) {
    message.error(reason);
  } else {
    message.success('已成功退出登录');
  }
  
  // 跳转到登录页
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname + window.location.search;
    
    // 避免在登录页重复跳转
    if (!currentPath.includes('/user/login')) {
      setTimeout(() => {
        window.location.href = '/user/login';
      }, 500); // 延迟500ms确保message显示
    }
  }
}

/**
 * 处理Token过期
 */
export function handleTokenExpired(): void {
  forceLogout('登录已过期，请重新登录');
}

// 开发环境下暴露调试函数到全局
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).tokenDebug = {
    getToken,
    getAuthHeader,
    isTokenValid,
    clearToken,
    setToken,
    checkLocalStorage: () => ({
      admin_access_token: localStorage.getItem('admin_access_token'),
      admin_refresh_token: localStorage.getItem('admin_refresh_token'),
      admin_token_type: localStorage.getItem('admin_token_type'),
      admin_token_expires: localStorage.getItem('admin_token_expires'),
    })
  };
  console.log('🔐 Token调试函数已添加到 window.tokenDebug');
}